import { Home, RefreshCw, User, Users } from 'lucide-react';
import type { ElementType } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { useTranslation } from '@/hooks/useTranslation';

interface MobileNavItem {
  to: string;
  labelKey: string;
  icon: ElementType;
  end?: boolean;
  /** Extra path prefixes that also mark this tab active (sibling sub-flows). */
  activeMatch?: string[];
}

/**
 * ResidentMobileNav — phone-native bottom tab bar for the resident surface.
 *
 * Mirrors the guard app's mobile bottom-nav pattern (GuardLayout) so the
 * installed PWA feels native instead of presenting a desktop sidebar squeezed
 * onto a phone. Rendered only below the mobile breakpoint by `PortalLayout`.
 *
 * Active state is computed explicitly (not via NavLink's function `className`)
 * to match `PortalLayout` and stay robust. Targets meet the 44px minimum and
 * the bar respects the device bottom safe-area inset.
 */

const TABS: MobileNavItem[] = [
  { to: '/resident', labelKey: 'resident.mobileNav.home', icon: Home, end: true },
  {
    to: '/resident/visitors',
    labelKey: 'resident.mobileNav.visitors',
    icon: Users,
    activeMatch: ['/resident/invite', '/resident/events'],
  },
  { to: '/resident/passes', labelKey: 'resident.mobileNav.passes', icon: RefreshCw },
  { to: '/resident/account', labelKey: 'resident.mobileNav.account', icon: User },
];

export function ResidentMobileNav() {
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <nav
      aria-label="Primary"
      data-slot="resident-mobile-nav"
      className="fixed inset-x-0 bottom-0 z-40 flex border-t border-[var(--border-default)] bg-[var(--bg-surface)] pb-[env(safe-area-inset-bottom)]"
    >
      {TABS.map((tab) => {
        const isActive =
          tab.end === true
            ? location.pathname === tab.to
            : location.pathname === tab.to || location.pathname.startsWith(`${tab.to}/`);
        const matchedSibling = tab.activeMatch?.some(
          (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
        );
        const active = isActive || matchedSibling;

        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end === true}
            data-active={active ? 'true' : undefined}
            className={`flex min-h-[44px] min-w-[44px] flex-1 flex-col items-center justify-center gap-1 py-2 transition-colors ${
              active
                ? 'text-[var(--text-accent)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            <tab.icon className="size-5" aria-hidden />
            <span className="text-[10px] font-medium leading-tight">{t(tab.labelKey)}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
